import { LockClosedIcon } from '@heroicons/react/outline';
import { FaShopify } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';

/** Products the agent found in the store's catalog, as cards in the chat. */
export function ProductCards({
  products,
}: {
  products: { name: string; price: string; swatch: string }[];
}) {
  return (
    <div className='grid w-[92%] grid-cols-2 gap-1.5'>
      {products.map(({ name, price, swatch }) => (
        <div
          key={name}
          className='overflow-hidden rounded-xl bg-white ring-1 ring-black/[0.07] dark:bg-white/[0.05] dark:ring-white/10'
        >
          <div className={clsxm('flex h-14 items-end justify-center', swatch)}>
            <span className='mb-2 h-9 w-7 rounded-t-md rounded-b-sm bg-white/85 shadow-[inset_0_-6px_0_rgba(0,0,0,0.08)]' />
          </div>
          <div className='px-2 py-1.5'>
            <p
              className={clsxm('truncate text-[11px] font-semibold', tone.ink)}
            >
              {name}
            </p>
            <p
              className={clsxm(
                'text-[10.5px] font-semibold tabular-nums',
                tone.quiet,
              )}
            >
              {price}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The store's own app in Shopify with the two read permissions, and the
 * agent's Shopify window with the shop connected and the chat code to paste.
 */
export function ShopifySetup({ shop }: { shop: string }) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[31rem] pb-2 sm:pb-24'>
      <div
        className={clsxm(
          'overflow-hidden rounded-[22px] text-start shadow-[0_40px_90px_-35px_rgba(30,20,90,0.55)]',
          tone.surface,
          tone.line,
        )}
      >
        <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-3.5 dark:border-white/[0.06]'>
          <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#f3f3f7] dark:bg-white'>
            <FaShopify className='h-5 w-5 text-[#95BF47]' />
          </span>
          <span className='min-w-0 flex-1'>
            <span
              className={clsxm('block text-[15px] font-semibold', tone.ink)}
            >
              Shopify
            </span>
            <span className={clsxm('block truncate text-xs', tone.quiet)}>
              <span dir='ltr'>{shop}</span>
            </span>
          </span>
          <span className='flex flex-none items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
            <CheckGlyph className='h-3.5 w-3.5' />
            {t('Connected')}
          </span>
        </div>
        <div className='space-y-4 px-5 py-5 text-[13px]'>
          <div>
            <p className={tone.quiet}>{t('Access')}</p>
            <div className='mt-1.5 flex flex-wrap gap-2'>
              {['read_products', 'read_orders'].map((scope) => (
                <span
                  key={scope}
                  dir='ltr'
                  className={clsxm(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[12px]',
                    tone.line,
                    tone.ink,
                  )}
                >
                  <CheckGlyph className='h-3.5 w-3.5 text-emerald-500' />
                  {scope}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className={tone.quiet}>{t('Chat widget embed code')}</p>
            <p
              dir='ltr'
              className='mt-1.5 truncate rounded-xl bg-[#0d0c1f] px-3.5 py-3 text-left font-mono text-[12px] text-[#c9c9e6]'
            >
              <span className='text-[#8f8fff]'>&lt;script</span>{' '}
              <span className='text-[#7dd3c0]'>src</span>
              =&quot;…/chat.min.js&quot;{' '}
              <span className='text-[#7dd3c0]'>defer</span>
              <span className='text-[#8f8fff]'>&gt;&lt;/script&gt;</span>
            </p>
            <p className={clsxm('mt-2 text-xs', tone.quiet)}>
              {t('Paste it into layout/theme.liquid, just before </body>.')}
            </p>
          </div>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 flex items-center gap-3 rounded-2xl p-3.5 pe-4 text-start shadow-[0_30px_60px_-28px_rgba(30,20,90,0.6)] sm:absolute sm:-bottom-2 sm:end-[-1.5rem] sm:mt-0 sm:w-[18rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#f3f3f7] dark:bg-white'>
          <FaShopify className='h-5 w-5 text-[#95BF47]' />
        </span>
        <span className='min-w-0'>
          <span className={clsxm('block text-[13px] font-semibold', tone.ink)}>
            {t('Easy-Peasy.AI agent')}
          </span>
          <span className={clsxm('block text-xs', tone.quiet)}>
            {t('Your own app, not from the App Store')}
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * An order lookup in the store's chat: the agent asks for the order number
 * and the checkout email, and shares the status only when both match.
 */
export function OrderLookup({
  brand,
  lines,
  order,
  status,
}: {
  brand: DemoBrand;
  lines: { from: 'visitor' | 'agent'; text: string }[];
  order: string;
  status: string;
}) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[30rem] pb-2 sm:pb-16'>
      <div className='mx-auto w-full overflow-hidden rounded-[18px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10 sm:w-[19rem] dark:bg-[#1c1b27]'>
        <div
          className={clsxm(
            'flex items-center gap-2.5 px-3.5 py-3 text-white',
            brand.bg,
          )}
        >
          <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/20 text-[12px] font-semibold'>
            {brand.initial}
          </span>
          <span className='truncate text-[13px] font-semibold'>
            {brand.name}
          </span>
        </div>
        <div className='space-y-2 px-3 py-3 text-[12.5px] leading-snug'>
          {lines.map(({ from, text }, index) =>
            from === 'visitor' ? (
              <div key={index} className='flex justify-end'>
                <p
                  className={clsxm(
                    'max-w-[85%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-3 py-2 dark:bg-white/[0.08]',
                    tone.ink,
                  )}
                >
                  {text}
                </p>
              </div>
            ) : (
              <p
                key={index}
                className={clsxm(
                  'max-w-[88%] rounded-2xl rounded-es-md px-3 py-2 text-white',
                  brand.bg,
                )}
              >
                {text}
              </p>
            ),
          )}
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 rounded-2xl p-4 text-start shadow-[0_30px_60px_-24px_rgba(0,0,0,0.65)] sm:absolute sm:-bottom-1 sm:end-0 sm:mt-0 sm:w-[16rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <p className='flex items-center justify-between gap-3'>
          <span className={clsxm('text-[13px] font-semibold', tone.ink)}>
            {t('Order')} <span dir='ltr'>{order}</span>
          </span>
          <span
            className={clsxm(
              'rounded-full px-2 py-0.5 text-[11px] font-semibold text-white',
              brand.bg,
            )}
          >
            {status}
          </span>
        </p>
        <p
          className={clsxm(
            'mt-2.5 flex items-center gap-1.5 text-xs',
            tone.quiet,
          )}
        >
          <LockClosedIcon className='h-3.5 w-3.5 flex-none' />
          {t('Shared after the order number and email matched')}
        </p>
      </div>
    </div>
  );
}
